// Centralize loader spinner and error messages,
import React, { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { create } from "zustand";
import { Loader as LoaderComponent } from "../components/loader";

export interface MessageQueueArgs {
  type: "error" | "info"
  message?: any
}

export interface MessageQueueItem extends MessageQueueArgs {
  key: number,
}

export interface LoadingState {
  isBusy: boolean
  Loader: ReactNode

  // Strict mode mounts components twice, but uses the prior state on second mount,
  // preventing the use of a stack to track loading state.
  // Keys must be unique for loading actions across all active components.
  loadingMap: Record<string, boolean>
  locationKey: string | undefined
  clearBusy: (key: string) => void
  resetBusy: () => void
  setBusy: (key: string) => void
  setIsBusy: (newIsBusy: boolean) => void
  setLoader: (newLoader: ReactNode) => void
  setLocationKey: (newLocationKey: string) => void

  // Message queue that holds errors and status updates.
  messageQueue: Array<MessageQueueItem>
  popMessageQueue: () => void
  pushMessageQueue: (message: MessageQueueArgs) => void

  reset: () => void
}

export const useLoadingStore = create<LoadingState>((set, get) => ({
  isBusy: false,
  loadingMap: {},
  locationKey: undefined,
  Loader: null,
  messageQueue: [],

  // --- Busy State Management ---
  clearBusy: (key: string) => {
    set((state: LoadingState) => {
      const { loadingMap } = state;
      delete loadingMap[key];
      return { loadingMap };
    });
  },
  resetBusy: () => {
    set((state: LoadingState) => ({ loadingMap: {} }));
  },
  setBusy: (key: string) => {
    set((state: LoadingState) => {
      const { loadingMap } = state;
      loadingMap[key] = true;
      return { loadingMap };
    });
  },
  setIsBusy: (newIsBusy: boolean) => {
    set((state: LoadingState) => ({ isBusy: newIsBusy }));
  },
  setLoader: (newLoader: ReactNode) => {
    set((state: LoadingState) => ({ Loader: newLoader }));
  },
  setLocationKey: (newLocationKey: string) => {
    set((state: LoadingState) => ({ locationKey: newLocationKey }));
  },

  // --- Status/Error Message Queue ---
  popMessageQueue: () => {
    set((state: LoadingState) => {
      // FIFO
      const { messageQueue } = state;
      return { messageQueue: messageQueue.slice(1) };
    });
  },
  pushMessageQueue: (message: MessageQueueArgs) => {
    set((state: LoadingState) => {
      const { messageQueue } = state;
      return { messageQueue: [...messageQueue, { ...message, key: new Date().getTime() }] };
    });
  },

  reset: () => {
    set({
      isBusy: false,
      loadingMap: {},
      Loader: null,
      messageQueue: [],
    });
  },
}));

export const useLoadingState = () => {
  const state = useLoadingStore();
  const { loadingMap, locationKey, resetBusy, setIsBusy, setLoader, setLocationKey } = state;

  // Determine if any loading is occurring
  const loadingKeys = Object.keys(loadingMap);
  useEffect(() => {
    const isBusy = Boolean(loadingKeys.length);
    setIsBusy(isBusy);
    setLoader(React.createElement(LoaderComponent, { isLoading: isBusy }));
  }, [loadingKeys.length, setIsBusy, setLoader]);

  // Navigating interrupts clearing of loading states; reset on location change.
  const location = useLocation();
  useEffect(() => {
    if (locationKey !== location.key) {
      setLocationKey(location.key);
      resetBusy();
    }
  }, [location.key, locationKey, setLocationKey, resetBusy]);

  return state;
}
